import React, { useCallback, useEffect, useRef, useState } from 'react';
import './AppAlert.css';
import {
  APP_ALERT_EVENT,
  type AppAlertOptions,
  type AppAlertType,
} from './appAlert';

interface QueuedAlert extends AppAlertOptions {
  id: number;
}

const DEFAULT_TITLES: Record<AppAlertType, string> = {
  success: 'Success',
  error: 'Something went wrong',
  warning: 'Heads up',
  info: 'Notice',
};

const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 12.4l2.6 2.6L16 9.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 4l8.5 15H3.5L12 4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M12 10v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="16.6" r="1" fill="currentColor" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="7.6" r="1" fill="currentColor" />
  </svg>
);

const ICONS: Record<AppAlertType, React.FC> = {
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
};

const AppAlertHost: React.FC = () => {
  const [queue, setQueue] = useState<QueuedAlert[]>([]);
  const idRef = useRef(0);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  const current = queue[0] ?? null;

  const finish = useCallback((confirmed: boolean) => {
    setQueue((prev) => {
      const [head, ...rest] = prev;
      if (head?._resolve) {
        head._resolve(confirmed);
      }
      return rest;
    });
  }, []);

  const dismiss = useCallback(() => {
    // Escape / overlay = stay (safer default for confirm dialogs).
    finish(true);
  }, [finish]);

  useEffect(() => {
    const handleAlert = (event: Event) => {
      const detail = (event as CustomEvent<AppAlertOptions>).detail;
      if (!detail || !detail.message) {
        return;
      }
      idRef.current += 1;
      setQueue((prev) => [...prev, { id: idRef.current, ...detail }]);
    };

    window.addEventListener(APP_ALERT_EVENT, handleAlert as EventListener);
    return () => window.removeEventListener(APP_ALERT_EVENT, handleAlert as EventListener);
  }, []);

  useEffect(() => {
    if (!current) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        // Stay / dismiss safely
        finish(true);
        return;
      }
      if (event.key === 'Enter' && !current.cancelText) {
        event.preventDefault();
        finish(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    const focusTimer = window.setTimeout(() => confirmRef.current?.focus(), 30);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [current, finish]);

  if (!current) {
    return null;
  }

  const type: AppAlertType = current.type ?? 'info';
  const Icon = ICONS[type];
  const title = current.title || DEFAULT_TITLES[type];

  return (
    <div
      className="app-alert-overlay"
      role="presentation"
      onClick={() => finish(true)}
    >
      <div
        className={`app-alert app-alert--${type}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="app-alert-title"
        aria-describedby="app-alert-message"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="app-alert__icon" aria-hidden="true">
          <Icon />
        </span>
        <h2 id="app-alert-title" className="app-alert__title">
          {title}
        </h2>
        <p id="app-alert-message" className="app-alert__message">
          {current.message}
        </p>
        <div className={`app-alert__actions${current.cancelText ? ' app-alert__actions--split' : ''}`}>
          {current.cancelText ? (
            <button
              type="button"
              className="app-alert__btn app-alert__btn--ghost"
              onClick={() => finish(false)}
            >
              {current.cancelText}
            </button>
          ) : null}
          <button
            type="button"
            ref={confirmRef}
            className="app-alert__btn"
            onClick={() => finish(true)}
          >
            {current.confirmText || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppAlertHost;
